// Explore is a uniform app grid with occasional full-width rows. The app list
// (recommendation order) and the row layout are separate: this module only
// decides where special rows go, never which apps are shown or their order.

export type ExploreRow<App> =
  | { type: "organic"; apps: App[] }
  | { type: "featured"; featureId: string }
  | { type: "sponsored"; placementId: string };

/** Where one kind of special row goes, counted in organic apps only. */
export interface PlacementPolicy {
  /** Organic apps before the first row. */
  firstAfter: number;
  /** Organic apps between later rows. */
  interval: number;
  maxPlacements: number;
}

/**
 * v0 Featured spacing by grid width (8 / 24 / 40 apps on four columns). Kept
 * here as the single place to tune; impressions, clicks and Run reach decide
 * later values.
 */
export const FEATURED_POLICIES = {
  desktop: { firstAfter: 8, interval: 16, maxPlacements: 3 },
  tablet: { firstAfter: 6, interval: 12, maxPlacements: 3 },
  mobile: { firstAfter: 4, interval: 8, maxPlacements: 3 },
} as const satisfies Record<string, PlacementPolicy>;

export function featuredPolicyFor(columns: number): PlacementPolicy {
  if (columns >= 4) return FEATURED_POLICIES.desktop;
  if (columns >= 2) return FEATURED_POLICIES.tablet;
  return FEATURED_POLICIES.mobile;
}

export interface PlacementStream {
  type: "featured" | "sponsored";
  ids: readonly string[];
  policy: PlacementPolicy;
}

type Slot = { type: PlacementStream["type"]; id: string; after: number };

/**
 * Interleave special rows into the app list.
 *
 * - Positions count organic apps only and are rounded up to whole grid lines
 *   (`columns`), so a row of cards is never split.
 * - Featured wins a tie; any special row that would sit within one grid line
 *   of the previous one moves down a line, so Featured and sponsored rows
 *   never touch.
 * - A row is placed only once the apps before it exist, so loading more apps
 *   appends below and never moves a row already on screen. No ids, no row:
 *   there are never empty slots.
 */
export function exploreRows<App>(
  apps: readonly App[],
  { columns = 4, streams = [] }: { columns?: number; streams?: readonly PlacementStream[] } = {},
): ExploreRow<App>[] {
  const line = Math.max(1, Math.floor(columns));
  const snap = (count: number) => Math.ceil(count / line) * line;
  const wanted: Slot[] = streams.flatMap((stream) =>
    stream.ids.slice(0, stream.policy.maxPlacements).map((id, index) => ({
      type: stream.type,
      id,
      after: snap(stream.policy.firstAfter + index * stream.policy.interval),
    })));
  wanted.sort((a, b) => a.after - b.after || (a.type === "featured" ? -1 : 1) - (b.type === "featured" ? -1 : 1));
  const slots: Slot[] = [];
  for (const slot of wanted) {
    const previous = slots.at(-1);
    const after = previous && slot.after < previous.after + line ? previous.after + line : slot.after;
    if (after > apps.length) break;
    slots.push({ ...slot, after });
  }
  const rows: ExploreRow<App>[] = [];
  let index = 0;
  for (const slot of slots) {
    if (slot.after > index) rows.push({ type: "organic", apps: apps.slice(index, slot.after) });
    rows.push(slot.type === "featured" ? { type: "featured", featureId: slot.id } : { type: "sponsored", placementId: slot.id });
    index = slot.after;
  }
  if (index < apps.length) rows.push({ type: "organic", apps: apps.slice(index) });
  return rows;
}

/**
 * Features for the current Explore view: none in search results, and within a
 * category only that category's (Bugs is a content type, as in the chips).
 */
export function featuresForView<Feature extends { card: { category?: string | null; contentType?: string | null } }>(
  features: readonly Feature[],
  { query, category }: { query: string; category: string | null },
): Feature[] {
  if (query.trim()) return [];
  if (!category) return [...features];
  return features.filter(({ card }) => (category === "bugs" ? card.contentType === "bug_reproduction" : card.category === category));
}
